import { Card, Badge, Button, Modal, Box, InlineStack, Text } from '@shopify/polaris';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useLookupStore } from '../../../context/useStore';
import ReactJsonViewCompare from 'react-json-view-compare';
import { CardHeading } from '../cards/CardHeading';

// refreshToken: bump it to refetch after an action on the page (lifecycle transitions, uploads)
// embedded: render without the card (e.g. inside a tab of another card), with an empty state instead of nothing
export default function EventTimeLine({ resourceName, resourceId, refreshToken = 0, embedded = false }) {
  const users = useLookupStore((state) => state.users);
  const [selectedEvents, setSelectedEvents] = useState([]);

  async function fetchEvents() {
    if (resourceId === undefined || resourceId === 'new') return;

    try {
      const response = await fetch(`/api/events/${resourceName}/${resourceId}`);
      const events = await response.json();
      if (events) setSelectedEvents(events);
    } catch (err) {
      console.error('Network error fetching events:', err);
    }
  }

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceName, resourceId, refreshToken]);

  if (resourceId === undefined || resourceId === 'new') return embedded ? <Text tone="subdued">The timeline starts once this has been saved.</Text> : <></>;
  if (selectedEvents.length === 0) return embedded ? <Text tone="subdued">No activity yet.</Text> : null;

  const Container = embedded ? Fragment : Card;

  return (
    <Container>
      {embedded ? null : <CardHeading title="Timeline" />}
      <div style={embedded ? { overflowY: 'auto', maxHeight: '520px' } : { overflowY: selectedEvents?.length > 24 ? 'auto' : 'hidden', maxHeight: '800px', paddingTop: '5px' }}>
        {Array.isArray(selectedEvents) &&
          selectedEvents?.map((event, idx) => {
            const eventData = parseEventData(event.data);
            const customerSummary = event.type === 'CUSTOMER_PORTAL_ACTIVITY' ? getCustomerPortalSummary(eventData) : null;
            const bankSummary = event.type.startsWith('BANK_PAYMENT') ? getBankPaymentSummary(event.type, eventData) : null;
            const approvalSummary = isApprovalEvent(event.type) ? getApprovalSummary(event.type, eventData) : null;
            const inquirySummary = isInquiryEvent(event.type) ? getInquirySummary(event.type, eventData) : null;
            const badgeTone = approvalSummary
              ? approvalSummary.tone
              : inquirySummary
              ? inquirySummary.tone
              : event.type === 'CUSTOMER_PORTAL_ACTIVITY'
              ? 'info'
              : event.type === 'BANK_PAYMENT_REVERTED' || event.type === 'BANK_PAYMENT_CANCELLED'
              ? 'warning'
              : event.type === 'BANK_PAYMENT_INITIATED'
              ? 'info'
              : 'success';
            const badgeLabel =
              event.type === 'CUSTOMER_PORTAL_ACTIVITY'
                ? customerSummary.label
                : approvalSummary
                ? approvalSummary.label
                : inquirySummary
                ? inquirySummary.label
                : bankSummary
                ? bankSummary.label
                : event.type === 'CREATE'
                ? 'create'
                : event.type === 'UPDATE'
                ? 'update'
                : event.type === 'DELETE'
                ? 'delete'
                : event.type === 'MAIL'
                ? 'mail sent'
                : event.type;
            return (
              <Box key={'event-' + idx} paddingBlockEnd={'100'}>
                <InlineStack gap="300">
                  <Text>
                    {event.type === 'CREATE' || event.type === 'REMINDER_CREATED'
                      ? '🎉'
                      : event.type === 'UPDATE'
                      ? '🛠'
                      : event.type === 'DELETE'
                      ? '❌'
                      : event.type === 'EMAIL_SENT' || event.type === 'REMINDER_SENT'
                      ? '✉️'
                      : event.type === 'CUSTOMER_PORTAL_ACTIVITY'
                      ? '👁️'
                      : event.type.startsWith('BANK_')
                      ? '🏦'
                      : event.type.startsWith('PURCHASE_ORDER_')
                      ? '🔗'
                      : event.type === 'INQUIRY_QUOTATION_RECEIVED'
                      ? '📄'
                      : event.type === 'INQUIRY_STATUS_CHANGED'
                      ? '🔁'
                      : isApprovalEvent(event.type)
                      ? '📋'
                      : '📣'}
                  </Text>
                  <Text>{users.find((user) => user.GebruikerID === event.user_id)?.Afkorting || (event.user_id === 0 ? (bankSummary ? 'Auto' : 'External') : '-')}</Text>
                  <Text>
                    <Badge tone={badgeTone}>{badgeLabel}</Badge>
                  </Text>
                  <Text>{getNiceDate(event.timestamp * 1)}</Text>
                  {bankSummary?.detail && (
                    <Text tone="subdued" as="span">
                      {bankSummary.detail}
                    </Text>
                  )}
                  {approvalSummary?.detail && (
                    <Text tone="subdued" as="span">
                      {approvalSummary.detail}
                    </Text>
                  )}
                  {inquirySummary?.detail && (
                    <Text tone="subdued" as="span">
                      {inquirySummary.detail}
                    </Text>
                  )}
                  {customerSummary?.detail && (
                    <Text tone="subdued" as="span">
                      {customerSummary.detail}
                    </Text>
                  )}
                  {getChangedFields(event, idx, selectedEvents)}
                  <PayloadModal event={event} idx={idx} selectedEvents={selectedEvents} />
                </InlineStack>
              </Box>
            );
          })}
      </div>
    </Container>
  );
}

export function getNiceDate(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();

  const secondsAgo = Math.floor((now - date) / 1000);
  const minutesAgo = Math.floor(secondsAgo / 60);
  const hoursAgo = Math.floor(minutesAgo / 60);

  if (secondsAgo < 60) return 'Just now';
  if (minutesAgo < 2) return '1 minute ago';
  if (minutesAgo < 60) return `${minutesAgo} minutes ago`;
  if (hoursAgo < 2) return '1 hour ago';
  if (date.toDateString() === now.toDateString()) return `${hoursAgo} hour(s) ago`;

  // Yesterday
  let yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${date.toLocaleTimeString()}`;
  }

  // For days older than yesterday
  let options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
  return date.toLocaleString(undefined, options);
}

function parseEventData(data) {
  if (!data) return null;
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch (err) {
    return null;
  }
}

function getCustomerPortalSummary(eventData) {
  const action = eventData?.action || 'ACTIVITY';
  const version = eventData?.quotationVersion ? `v${eventData.quotationVersion}` : null;

  if (action === 'PORTAL_OPENED') {
    return { label: 'Portal opened', detail: 'Customer opened the shared quotation portal' };
  }

  if (action === 'PDF_VIEWED') {
    const source = eventData?.source === 'live_fallback' ? 'live fallback' : 'stored snapshot';
    return { label: 'PDF viewed', detail: `Viewed ${version || 'quotation PDF'} (${source})` };
  }

  if (action === 'COMPARE_VIEWED') {
    const fromVersion = eventData?.quotationAVersion ? `v${eventData.quotationAVersion}` : '?';
    const toVersion = eventData?.quotationBVersion ? `v${eventData.quotationBVersion}` : '?';
    return { label: 'Compared versions', detail: `Compared ${fromVersion} -> ${toVersion}` };
  }

  if (action === 'ACCEPTANCE_SUBMITTED') {
    const acceptedOptionalCount = Number(eventData?.acceptedOptionalCount || 0);
    const optionalSuffix = acceptedOptionalCount > 0 ? `, ${acceptedOptionalCount} optional selected` : '';
    return { label: 'Acceptance submitted', detail: `Accepted ${version || 'quotation'}${optionalSuffix}` };
  }

  return {
    label: String(action).toLowerCase().replace(/_/g, ' '),
    detail: version ? `Interaction on ${version}` : 'Customer portal activity'
  };
}

function getBankPaymentSummary(type, eventData) {
  const amount = eventData?.amount != null ? `€ ${Number(eventData.amount).toFixed(2)}` : '';
  const counterpart = eventData?.counterpart ? ` from ${eventData.counterpart}` : '';

  if (type === 'BANK_PAYMENT_BOOKED') {
    const how = eventData?.via && eventData.via !== 'manual match' ? ` (${eventData.via})` : '';
    return {
      label: eventData?.fullyPaid ? 'Bank payment booked — paid' : 'Bank payment booked (partial)',
      detail: `${amount}${counterpart}${how}`
    };
  }
  if (type === 'BANK_PAYMENT_REVERTED') {
    return { label: 'Bank payment reverted', detail: `${amount} unbooked (unmatch)` };
  }
  if (type === 'BANK_PAYMENT_INITIATED') {
    const when = eventData?.executionDate ? ` · execution ${eventData.executionDate}` : '';
    return {
      label: 'Payment sent to bank',
      detail: `${amount} to ${eventData?.creditorName || 'supplier'} (${eventData?.creditorIban || '?'}) via Ponto${when} — invoice stays open until the debit is matched`
    };
  }
  if (type === 'BANK_PAYMENT_CANCELLED') {
    return { label: 'Bank payment cancelled', detail: `${amount} withdrawn before signature — invoice still open` };
  }
  return { label: type.toLowerCase().replace(/_/g, ' '), detail: '' };
}

function isApprovalEvent(type) {
  return String(type).startsWith('APPROVAL_') || String(type).startsWith('PURCHASE_ORDER_') || type === 'INTAKE_ENRICHED' || type === 'PAYMENT_APPROVAL_WARNING';
}

// approval flow events (services/approval on the server)
function getApprovalSummary(type, data = {}) {
  const note = data?.note ? ` · ${data.note}` : '';
  switch (type) {
    case 'APPROVAL_ASSIGNED':
      return { label: data?.approveNow ? 'Approved directly' : 'Assigned for approval', tone: 'info', detail: `to ${data?.assigneeAfkorting || data?.assigneeId || '?'}${note}` };
    case 'APPROVAL_REASSIGNED':
      return { label: data?.handOff ? 'Part handed off' : 'Reassigned', tone: 'info', detail: `to ${data?.toAfkorting || (data?.toUserIds || [data?.toUserId]).filter(Boolean).join(', ')}${note}` };
    case 'APPROVAL_ALLOCATION_SAVED':
      return { label: 'Cost split saved', tone: 'info', detail: `${data?.lineCount ?? ''} line(s)` };
    case 'APPROVAL_APPROVED':
      return {
        label: data?.onBehalf ? 'Approved (on behalf)' : 'Approved',
        tone: 'success',
        detail: `${data?.lineIds?.length || 1} line(s)${data?.amount != null ? ` · € ${Number(data.amount).toFixed(2)}` : ''}${note}`
      };
    case 'APPROVAL_REJECTED':
      return { label: 'Rejected', tone: 'critical', detail: data?.reason || '' };
    case 'APPROVAL_REOPENED':
      return { label: 'Reopened for approval', tone: 'warning', detail: data?.note || '' };
    case 'APPROVAL_STATUS_CHANGED':
      return { label: 'Approval status', tone: data?.to === 'APPROVED' ? 'success' : data?.to === 'REJECTED' ? 'critical' : 'info', detail: `${data?.from || '-'} → ${data?.to || '?'}` };
    case 'PURCHASE_ORDER_LINKED':
      return { label: 'Purchase order linked', tone: 'info', detail: `PO ${data?.purchaseOrderNumber || data?.purchaseOrderId || ''}${data?.source ? ` (${String(data.source).toLowerCase()})` : ''}` };
    case 'PURCHASE_ORDER_UNLINKED':
      return { label: 'Purchase order unlinked', tone: 'warning', detail: `PO ${data?.purchaseOrderNumber || data?.purchaseOrderId || ''}` };
    case 'INTAKE_ENRICHED':
      return {
        label: 'Document analysed',
        tone: 'info',
        detail: `${data?.textSource || 'no text'}${data?.autoLinked?.length ? ` · linked PO ${data.autoLinked.join(', ')}` : ''}${data?.suggestedAssigneeId ? ' · approver suggested' : ''}`
      };
    case 'PAYMENT_APPROVAL_WARNING':
      return { label: 'Paid while not approved', tone: 'critical', detail: `via ${data?.via || '?'} · status ${data?.approvalStatus || 'unknown'}` };
    default:
      return { label: String(type).toLowerCase().replace(/_/g, ' '), tone: 'info', detail: '' };
  }
}

function isInquiryEvent(type) {
  return String(type).startsWith('INQUIRY_');
}

// inquiry lifecycle events (services/inquiryLifecycle.js on the server)
const INQUIRY_STATUS_TONES = { SENT: 'info', QUOTATION_RECEIVED: 'attention', ORDERED: 'success' };
const INQUIRY_VIA_LABELS = {
  MANUAL: 'lifecycle card',
  STATUS_EDIT: 'status editor',
  PDF_EMAILED: 'PDF emailed',
  FORM: 'form save',
  QUOTATION_UPLOADED: 'quotation upload',
  CREATE: 'on creation'
};

function getInquirySummary(type, data = {}) {
  if (type === 'INQUIRY_STATUS_CHANGED') {
    const via = INQUIRY_VIA_LABELS[data?.via] || (data?.via ? String(data.via).toLowerCase().replace(/_/g, ' ') : '');
    return {
      label: `Status: ${data?.toName || data?.to || '?'}`,
      tone: INQUIRY_STATUS_TONES[data?.to] || 'info',
      detail: `${data?.fromName || '-'} → ${data?.toName || '?'}${via ? ` (${via})` : ''}${data?.note ? ` · ${data.note}` : ''}`
    };
  }
  if (type === 'INQUIRY_QUOTATION_RECEIVED') {
    const summary = data?.summary || {};
    const amount =
      summary.totalExclVat !== null && summary.totalExclVat !== undefined
        ? `${summary.currency && summary.currency !== 'EUR' ? `${summary.totalExclVat} ${summary.currency}` : `€ ${Number(summary.totalExclVat).toFixed(2)}`} excl. VAT`
        : null;
    const facts = [data?.filename, summary.quotationNumber ? `N° ${summary.quotationNumber}` : null, amount, summary.validUntil ? `valid until ${summary.validUntil}` : null].filter(Boolean);
    return { label: 'Quotation received', tone: 'attention', detail: facts.join(' · ') };
  }
  return { label: String(type).toLowerCase().replace(/_/g, ' '), tone: 'info', detail: '' };
}

function diffKeys(obj1, obj2) {
  let diffKeys = [];

  // Get all the keys from both objects
  let keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  // Check if the values for each key are the same in both objects
  keys.forEach((key) => {
    if (obj1[key] !== obj2[key]) {
      diffKeys.push(key);
    }
  });

  return diffKeys;
}

function getChangedFields(event, idx, selectedEvents) {
  // if (idx === 0) return;
  if (event.type !== 'UPDATE') return;

  const previousEvent = selectedEvents[idx + 1];
  if (previousEvent?.data === undefined || previousEvent.type !== 'UPDATE') return;

  const currentData = parseEventData(event.data);
  const previousData = parseEventData(selectedEvents[idx + 1]?.data);
  if (!currentData || !previousData) return;

  return diffKeys(currentData, previousData).map((key, idx) => {
    return (
      <div key={`subkey-${key}`}>
        <Badge key={idx}>{key}</Badge>
      </div>
    );
  });
}

const PayloadModal = ({ event, idx, selectedEvents }) => {
  const [active, setActive] = useState(false);
  const buttonRef = useRef(null);

  const eventData = parseEventData(event.data);

  let previousEventSameType = null;
  if (event.type === 'UPDATE') previousEventSameType = selectedEvents.slice(idx + 1).find((e) => e.type === event.type || e.type === 'CREATE');
  else previousEventSameType = selectedEvents.slice(idx + 1).find((e) => e.type === event.type);

  if (!event?.data) return null;

  const activator = (
    <div ref={buttonRef}>
      <Button size="micro" onClick={() => setActive(true)}>
        🔖
      </Button>
    </div>
  );

  if (event.type === 'EMAIL_SENT') {
    return (
      <>
        {activator}
        <Modal activator={buttonRef} open={active} size="large" onClose={() => setActive(false)} title="Email sent event">
          <Modal.Section>TO: {eventData?.toAddress}</Modal.Section>
          <Modal.Section>CC: {eventData?.cc?.join(', ')}</Modal.Section>
          <Modal.Section>BCC: {eventData?.bcc?.join(', ')}</Modal.Section>
          <Modal.Section>{eventData?.emailSubject}</Modal.Section>
          <Modal.Section>
            <div style={{ font: 'inherit' }}>
              <pre>{eventData?.emailBody}</pre>
            </div>
          </Modal.Section>
          <Modal.Section>Emailed docs: {eventData?.sharepointDocs?.map((doc) => doc.sharepointDocName).join(', ')}</Modal.Section>
        </Modal>
      </>
    );
  }

  if (!previousEventSameType)
    return (
      <>
        {activator}
        <Modal activator={buttonRef} open={active} size="large" onClose={() => setActive(false)} title="Event">
          <Modal.Section>
            <pre>{JSON.stringify(eventData, null, 2)}</pre>
          </Modal.Section>
        </Modal>
      </>
    );

  // if eventTypeIsUpdateAndACreateEventExists then create the update with the create event

  // compare if event with same type exists that happend before this event
  return (
    <>
      {activator}
      <Modal activator={buttonRef} open={active} size="large" onClose={() => setActive(false)} title="Event">
        <Modal.Section>
          <ReactJsonViewCompare newData={parseEventData(event?.data)} oldData={parseEventData(previousEventSameType?.data)} />
        </Modal.Section>
      </Modal>
    </>
  );
};
